/**
 * Confirm enabling/disabling a cron job with audit reason.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/app/store';
import { patchJobThunk, selectPatchingJob } from '@/app/store/jobsSlice';
import type { Job } from '@/app/types/jobs';
import { Modal, Button } from '@/components/common/Modal';
import { TextArea } from '@/components/common/FormField';

import './JobToggleModal.scss';

export const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 500;

export interface JobToggleModalProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
}

function stateLabel(enabled: boolean): string {
  return enabled ? 'Enabled' : 'Disabled';
}

export const JobToggleModal: React.FC<JobToggleModalProps> = ({ job, isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isPatching = useSelector((state: RootState) =>
    job != null ? selectPatchingJob(state) === job.job_name : false
  );

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setReasonError(undefined);
      setSubmitError(null);
    }
  }, [isOpen, job?.job_name]);

  const proposedEnabled = job != null ? !job.enabled : false;
  const reasonTrimmed = reason.trim();
  const submitDisabled =
    job == null ||
    isPatching ||
    reasonTrimmed.length < MIN_REASON_LENGTH ||
    reasonTrimmed.length > MAX_REASON_LENGTH;

  const validateReason = useCallback((): boolean => {
    if (reasonTrimmed.length < MIN_REASON_LENGTH) {
      setReasonError(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
      return false;
    }
    if (reasonTrimmed.length > MAX_REASON_LENGTH) {
      setReasonError(`Reason must be at most ${MAX_REASON_LENGTH} characters`);
      return false;
    }
    setReasonError(undefined);
    return true;
  }, [reasonTrimmed.length]);

  const handleReasonChange = (value: string) => {
    setReason(value);
    if (reasonError) setReasonError(undefined);
    if (submitError) setSubmitError(null);
  };

  const handleReasonBlur = () => {
    const t = reason.trim();
    if (t.length > 0 && t.length < MIN_REASON_LENGTH) {
      setReasonError(`Reason must be at least ${MIN_REASON_LENGTH} characters`);
    }
  };

  const handleSubmit = async () => {
    if (job == null) return;
    if (!validateReason()) return;
    setSubmitError(null);
    try {
      await dispatch(
        patchJobThunk({
          jobName: job.job_name,
          enabled: proposedEnabled,
          reason: reasonTrimmed,
        })
      ).unwrap();
      onClose();
    } catch (err) {
      setSubmitError(typeof err === 'string' ? err : 'Failed to update job');
    }
  };

  if (job == null) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Toggle cron job"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPatching}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={submitDisabled}>
            {isPatching ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="job-toggle-modal">
        <div className="job-toggle-modal__row">
          <span className="job-toggle-modal__label">Job</span>
          <span className="job-toggle-modal__value">{job.job_name}</span>
        </div>
        <div className="job-toggle-modal__row">
          <span className="job-toggle-modal__label">Current state</span>
          <span className="job-toggle-modal__value">{stateLabel(job.enabled)}</span>
        </div>
        <div className="job-toggle-modal__row">
          <span className="job-toggle-modal__label">After save</span>
          <span className="job-toggle-modal__value job-toggle-modal__value--emphasis">
            {stateLabel(proposedEnabled)}
          </span>
        </div>
        <TextArea
          label="Reason (audit trail)"
          value={reason}
          onChange={handleReasonChange}
          onBlur={handleReasonBlur}
          placeholder={`At least ${MIN_REASON_LENGTH} characters`}
          required
          error={reasonError}
          minLength={MIN_REASON_LENGTH}
          maxLength={MAX_REASON_LENGTH}
          rows={4}
          id="job-toggle-reason"
        />
        {submitError != null && submitError.length > 0 && (
          <p className="job-toggle-modal__error" role="alert">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default JobToggleModal;
